import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, CartModel, OrderLineModel, OrderModel } from "@insite/client-framework/Types/ApiModels";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import GridContainer, { GridContainerProps, GridOffset, GridWrapper } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getColor from "@insite/mobius/utilities/getColor";
import { StyledButton } from "@insite/mobius/Clickable";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import ADIColors from "@NA/Utils/ADIColors";
import ADIAdonisYesProductDescription, { ADIAdonisYesProductDescriptionStyles } from "./ADIAdonisYesProductDescription";
import ADIBuyItAgainButton from "./ADIBuyItAgainButton";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import ADIOrderDetailsProgressBar from "./ADIOrderDetailsProgressBar";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ProductDescription from "@NA/Overrides/Components/ProductDescription";
import ADIEcoFeeDisplay from "../ADIEcoFeeDisplay";
import ADISpecialHandlingFeeDisplay from "../ADISpecialHandlingFeeDisplay";

interface OwnProps {
    shipment: any;
    order?: OrderModel;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const currentCart = getCurrentCartState(state).value;
    const adiUserRoles = state.ADIGlobalData.ADIUserRoleStore;
    return {
        canReorderItems:
            getSettingsCollection(state).orderSettings.canReorderItems &&
            ((adiUserRoles.fullAccess && adiUserRoles.accounting) || adiUserRoles.fullAccess || adiUserRoles.employee),
        cart: currentCart,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface AdiOrderDetailsOrderLinesListStyles {
    gridContainer?: GridContainerProps;
    orderLineCardGridContainer?: GridContainerProps;
    orderLineCardImageGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    productImageLinkMobile?: LinkPresentationProps;
    orderLineCardImage?: LazyImageProps;
    orderLineCardImageMobile?: LazyImageProps;
    orderLineCardInfoGridItem?: GridItemProps;
    orderLineCardInfoGridContainer?: GridContainerProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productDescriptionStylesMobile?: InjectableCss;
    adiAdonisYesProductDescriptionStyles?: ADIAdonisYesProductDescriptionStyles;
    imgTextStyles?: InjectableCss;
    imgTextStylesMobile?: InjectableCss;
    orderLinesMainGridItem?: GridItemProps;
    orderDetailsHeader?: InjectableCss;
    mobileBuyItAgainButton?: GridItemProps;
    desktopBuyItAgainButton?: GridItemProps;
    orderLineCardImageGridItemMobile?: GridItemProps;
    orderLineCardInfoGridItemMobile?: GridItemProps;
    orderDetailsItemsText?: InjectableCss;
    orderDetailsNoText?: InjectableCss;
    orderDetailsQtyText?: InjectableCss;
    orderDetailsUnitPriceText?: InjectableCss;
    orderDetailsExtPriceText?: InjectableCss;
    manufacturerItemStyles?: InjectableCss;
    qtyFieldGridItem?: GridItemProps;
    unitPriceGridItem?: GridItemProps;
    orderLineCardInfoDescriptionGridItem?: GridItemProps;
    manufacturerSectionGridItem?: GridItemProps;
    extPriceGridItem?: GridItemProps;
    manufacturerItemMobileStyles?: InjectableCss;
    extPriceMobileStyles?: InjectableCss;
    unitPriceMobileStyles?: InjectableCss;
    qtyMobileStyles?: InjectableCss;
    manufacturerItemContainerMobileStyles?: InjectableCss;
    progressBarGridItem?: GridItemProps;
}

export const adiOrderDetailsOrderLinesListStyles: AdiOrderDetailsOrderLinesListStyles = {
    progressBarGridItem: {
        width: 12,
        printWidth: 12,
        css: css`
            @media only screen and (max-width: 1024px) {
                display: none;
            }
            @media print {
                .mobileProgressBar {
                    display: none;
                }
            }
        `,
    },
    manufacturerItemContainerMobileStyles: {
        css: css`
            @media print {
                display: none;
            }
        `,
    },
    productDescriptionStylesMobile: {
        css: css`
            @media print {
                display: none;
            }
        `,
    },
    qtyMobileStyles: {
        css: css`
            font-size: 14px;
            margin-top: 15px;
            @media print {
                display: none;
            }
        `,
    },
    unitPriceMobileStyles: {
        css: css`
            font-size: 12px;
            margin-top: 1px;
            @media print {
                display: none;
            }
        `,
    },
    extPriceMobileStyles: {
        css: css`
            margin-top: 5px;
            font-weight: bold;
            @media print {
                display: none;
            }
        `,
    },
    manufacturerItemMobileStyles: {
        css: css`
            font-size: 10px;
            color: ${ADIColors.gray4};
            @media print {
                display: none;
            }
        `,
    },
    manufacturerSectionGridItem: {
        width: 12,
        printWidth: 12,
    },
    orderLineCardInfoDescriptionGridItem: {
        width: 6,
        printWidth: 6,
        css: css`
            @media print {
                padding-right: 0px;
                padding-left: 30px;
            }
        `,
    },
    qtyFieldGridItem: {
        width: 2,
        printWidth: 2,
        css: css`
            font-size: 14px;
            justify-content: center;
            @media print {
                font-size: 10px;
                padding-right: 0px;
                padding-left: 0px;
            }
        `,
    },
    unitPriceGridItem: {
        width: 2,
        printWidth: 2,
        css: css`
            font-size: 14px;
            justify-content: flex-end;
            @media print {
                font-size: 10px;
                padding: 15px 10px 15px 0px;
            }
        `,
    },
    extPriceGridItem: {
        width: 2,
        printWidth: 2,
        css: css`
            font-size: 14px;
            font-weight: bold;
            justify-content: flex-end;
            @media print {
                font-size: 10px;
                padding: 15px 0px 15px 0px;
                margin-left: 20px;
            }
        `,
    },
    manufacturerItemStyles: {
        css: css`
            font-size: 12px;
            color: ${ADIColors.gray4};
            @media print {
                color: ${ADIColors.black};
            }
        `,
    },
    orderDetailsItemsText: {
        css: css`
            width: 11.493%;
        `,
    },
    orderDetailsNoText: {
        css: css`
            width: 74.275%;
        `,
    },
    orderDetailsQtyText: {
        css: css`
            width: 11.2%;
            padding-left: 10px;
        `,
    },
    orderDetailsUnitPriceText: {
        css: css`
            width: 18.38%;
            padding-left: 60px;
        `,
    },
    orderDetailsExtPriceText: {
        css: css`
            width: 16.1%;
            padding-left: 50px;
        `,
    },
    orderLineCardImageGridItemMobile: {
        printWidth: 0,
        css: css`
            display: none;
            @media only screen and (max-width: 576px) {
                display: flex;
                flex-basis: 38%;
                max-width: 38%;
                flex-direction: column;
                align-items: center;
            }
        `,
    },
    orderLineCardInfoGridItemMobile: {
        printWidth: 0,
        css: css`
            display: none;
            @media only screen and (max-width: 576px) {
                display: flex;
                flex-basis: 62%;
                max-width: 62%;
                flex-direction: column;
                > ${StyledButton} {
                    > ${TypographyStyle} {
                        font-size: 14px;
                        color: ${getColor("text.main")};
                        overflow: hidden;
                        text-overflow: ellipsis;
                        display: -webkit-box;
                        -webkit-line-clamp: 3;
                        -webkit-box-orient: vertical;
                        word-break: break-word;
                        line-height: 1.43;
                        font-weight: 600;
                    }
                }
            }
        `,
    },
    desktopBuyItAgainButton: {
        width: 12,
        printWidth: 0,
        css: css`
            @media print {
                display: none;
                padding: 0px;
            }
            justify-content: flex-end;
            padding-top: 0px;
        `,
    },
    mobileBuyItAgainButton: {
        width: 12,
        printWidth: 0,
        css: css`
            display: none;
            @media only screen and (max-width: 576px) {
                display: flex;
                border-top: 1px solid ${getColor("text.disabled")};
                padding: 0px;
                justify-content: center;
            }
        `,
    },
    orderDetailsHeader: {
        css: css`
            @media print {
                display: none;
            }
            display: none;
            @media screen and (min-width: 576px) and (max-width: 1024px) {
                width: 100%;
                min-height: 40px;
                display: flex;
                align-items: center;
                font-size: 16px;
                font-weight: bold;
                font-stretch: normal;
                font-style: normal;
                line-height: 1.13;
                letter-spacing: normal;
                color: ${getColor("text.main")};
                padding: 11px 20px 11px 20px;
                margin-top: 15px;
                margin-bottom: 15px;
                background-color: ${ADIColors.white2};
            }
        `,
    },
    orderLinesMainGridItem: {
        width: 12,
        printWidth: 12,
        css: css`
            flex-direction: column;
            @media print {
                padding: 15px 0px 15px 5px;
            }
            @media only screen and (max-width: 1024px) {
                margin: 0px 15px !important;
                padding-bottom: 5px;
            }
            @media only screen and (max-width: 576px) {
                margin: 0px !important;
                padding-top: 30px;
            }
            > ${GridWrapper} {
                &:last-child {
                    > ${GridOffset} {
                        border-bottom: none;
                        margin-bottom: 0px !important;
                    }
                }
            }
        `,
    },
    orderLineCardGridContainer: {
        css: css`
            > ${GridOffset} {
                margin-bottom: 20px !important;
                border-bottom: solid 1px ${getColor("text.disabled")};
                @media screen and (min-width: 1025px) {
                    width: 100%;
                }
                @media only screen and (max-width: 576px) {
                    background: ${getColor("common.background")};
                    margin-bottom: 30px !important;
                }
            }
        `,
    },
    imgTextStyles: {
        css: css`
            font-size: 10px;
            text-align: center;
            color: ${getColor("text.main")};
            border: 1px solid ${getColor("common.border")};
            padding: 0 6px;
            height: 79px;
            width: 79px;
            align-items: center;
            justify-content: center;
            display: flex;
            overflow: hidden;
            text-overflow: ellipsis;
            @media only screen and (max-width: 576px) {
                width: 115px;
                height: 96px;
            }
            @media print {
                width: 65px;
                height: 65px;
            }
        `,
    },
    imgTextStylesMobile: {
        css: css`
            font-size: 10px;
            text-align: center;
            color: ${getColor("text.main")};
            border: 1px solid ${getColor("common.border")};
            padding: 0 6px;
            height: 79px;
            width: 79px;
            align-items: center;
            justify-content: center;
            display: flex;
            overflow: hidden;
            text-overflow: ellipsis;
            @media only screen and (max-width: 576px) {
                width: 115px;
                height: 96px;
            }
            @media print {
                display: none;
            }
        `,
    },
    productImageLink: {
        css: css`
            width: 78px;
            height: 78px;
            @media only screen and (max-width: 576px) {
                justify-content: center;
                width: 127px;
            }
            @media print {
                width: 65px;
                height: 65px;
            }
        `,
    },
    productImageLinkMobile: {
        css: css`
            @media print {
                display: none;
            }
            width: 78px;
            height: 78px;
            @media only screen and (max-width: 576px) {
                justify-content: center;
                width: 127px;
            }
        `,
    },
    orderLineCardImage: {
        css: css`
            img {
                max-width: 78px;
                max-height: 78px;
                object-fit: contain;
                width: 100%;
                @media only screen and (max-width: 576px) {
                    width: 100% !important;
                    height: auto !important;
                    object-fit: contain;
                    max-height: 96px;
                    max-width: 127px;
                }
                @media print {
                    max-width: 65px;
                    max-height: 65px;
                    filter: grayscale(100%);
                }
            }
        `,
    },
    orderLineCardImageMobile: {
        css: css`
            img {
                max-width: 78px;
                max-height: 78px;
                object-fit: contain;
                width: 100%;
                @media print {
                    display: none;
                }
                @media only screen and (max-width: 576px) {
                    width: 100% !important;
                    height: auto !important;
                    object-fit: contain;
                    max-height: 96px;
                    max-width: 127px;
                }
            }
        `,
    },
    productInfoDescriptionGridItem: {
        width: 12,
        printWidth: 12,
        css: css``,
    },
    orderLineCardImageGridItem: {
        width: 2,
        printWidth: 2,
        css: css`
            @media screen and (min-width: 1025px) {
                flex-basis: auto;
            }
            @media only screen and (max-width: 576px) {
                display: none;
            }
        `,
    },
    orderLineCardInfoGridItem: {
        width: [10, 10, 10, 10, 10],
        printWidth: 10,
        css: css`
            @media only screen and (max-width: 576px) {
                display: none;
            }
            @media print {
                padding-right: 0px;
            }
        `,
    },
    productInfoBrandDescriptionGridContainer: {
        gap: 5,
    },
};

const styles = adiOrderDetailsOrderLinesListStyles;

const ADIOrderDetailsOrderLinesList: FC<Props> = ({ order, canReorderItems, cart, shipment }) => {
    if (!order || !order.orderLines) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.orderDetailsHeader}>
                <Typography {...styles.orderDetailsItemsText}>{translate("Items")}</Typography>
                <Typography {...styles.orderDetailsNoText}>{translate("")}</Typography>
                <Typography {...styles.orderDetailsQtyText}>{translate("Qty")}</Typography>
                <Typography {...styles.orderDetailsUnitPriceText}>{translate("Unit Price")}</Typography>
                <Typography {...styles.orderDetailsExtPriceText}>{translate("Ext. Price")}</Typography>
            </StyledWrapper>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.orderLinesMainGridItem}>
                    {shipment?.invoiceOrderLines.map((shipmentPackageLine: any, index: number) => (
                        <>
                            {order?.orderLines && (
                                <OrderLineCard
                                    shipmentPackageLine={shipmentPackageLine}
                                    orderLines={order?.orderLines}
                                    cart={cart}
                                    canReorderItems={canReorderItems}
                                    index={index}
                                />
                            )}
                        </>
                    ))}
                </GridItem>
                <GridItem {...styles.progressBarGridItem}>
                    <ADIOrderDetailsProgressBar shipment={shipment} order={order} />
                </GridItem>
            </GridContainer>
        </>
    );
};

const OrderLineCard = (props: {
    shipmentPackageLine: any;
    orderLines: OrderLineModel[];
    cart: CartModel | undefined;
    canReorderItems: boolean;
    index: number;
}) => {
    const { shipmentPackageLine, orderLines, cart, canReorderItems, index } = props;
    const isShareEntity = getIsShareEntity();
    const [product, setProduct] = useState<CartLineModel | undefined | null>(undefined);
    let [orderLine, setOrderLine] = useState<OrderLineModel>();
    const isAdonisYes = shipmentPackageLine?.properties?.isAdonis === "Yes";
   
    useEffect(() => {
        if (orderLines && shipmentPackageLine) {
            const newOrderLine = orderLines.find(
                item => item?.productErpNumber === shipmentPackageLine?.productErpNumber,
            );
            if (newOrderLine) {
                setOrderLine(newOrderLine);
            }
        }
    }, [orderLines, shipmentPackageLine]);

    useEffect(() => {
        if (orderLine && cart?.cartLines) {
            const product =
                cart && cart?.cartLines && cart?.cartLines?.find(item => item?.productId === orderLine?.productId);
            product ? setProduct(product) : setProduct(null);
        }
    }, [orderLine, cart, cart?.cartLines]);

    if (!orderLine) {
        return null;
    }

    const getunitOfMeasureDisplay = (uom: string) => {
        if (uom === null || uom === undefined || uom === "") {
            return "";
        } else {
            if (uom?.toLowerCase() === "each" || uom?.toLowerCase() === "ea") {
                return "";
            } else if (uom?.toLowerCase() === "package" || uom?.toLowerCase() === "pkg") {
                return "/pkg";
            } else if (uom?.toLowerCase() === "rl" || uom?.toLowerCase() === "roll") {
                return "/roll";
            } else {
                return "/" + uom;
            }
        }
    };

    const manufacturerCustomStyle = () => css`
        display: ${orderLine?.manufacturerItem === null || orderLine?.manufacturerItem === ""
            ? "none"
            : "inline-block"};
    `;

    const descriptionCustomStyle = () => css`
        > ${StyledButton} {
            width: 90%;
            &:hover {
                color: ${getColor("text.main")} !important;
                text-decoration: underline;
            }
            @media print {
                text-decoration: ${canReorderItems && orderLine && orderLine?.canAddToCart ? "underline" : "none"};
            }
            > ${TypographyStyle} {
                font-size: 16px;
                color: ${getColor("text.main")} !important;
                font-weight: 600;
                line-height: 22px;
                overflow: hidden;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                &:hover {
                    color: ${getColor("text.main")} !important;
                    text-decoration: underline;
                }
            }
        }
    `;

    return (
        <>
            {
                <>
                    {!isAdonisYes && (
                        <GridContainer {...styles.orderLineCardGridContainer} key={index}>
                            <GridItem {...styles.orderLineCardImageGridItem}>
                                {shipmentPackageLine?.isActiveProduct && (
                                    <Link
                                        {...styles.productImageLink}
                                        href={`/product/${shipmentPackageLine?.productErpNumber}`}
                                    >
                                        {shipmentPackageLine?.properties?.smallImagePath?.indexOf("SM_NotFound.jpg") ===
                                            -1 && (
                                            <LazyImage
                                                {...styles.orderLineCardImage}
                                                src={shipmentPackageLine?.properties?.smallImagePath}
                                                isShareEntity={isShareEntity}
                                            />
                                        )}
                                        {shipmentPackageLine != undefined &&
                                            shipmentPackageLine?.properties?.smallImagePath?.indexOf(
                                                "SM_NotFound.jpg",
                                            ) !== -1 && (
                                                <Typography {...styles.imgTextStyles}>
                                                    {shipmentPackageLine?.ManufacturerItem}
                                                </Typography>
                                            )}
                                    </Link>
                                )}
                                {!shipmentPackageLine?.isActiveProduct && (
                                    <Link {...styles.productImageLink}>
                                        {shipmentPackageLine?.properties?.smallImagePath?.indexOf("SM_NotFound.jpg") ===
                                            -1 && (
                                            <LazyImage
                                                {...styles.orderLineCardImage}
                                                src={shipmentPackageLine?.properties?.smallImagePath}
                                                isShareEntity={isShareEntity}
                                            />
                                        )}
                                        {shipmentPackageLine != undefined &&
                                            shipmentPackageLine?.properties?.smallImagePath?.indexOf(
                                                "SM_NotFound.jpg",
                                            ) !== -1 && (
                                                <Typography {...styles.imgTextStyles}>
                                                    {shipmentPackageLine?.ManufacturerItem}
                                                </Typography>
                                            )}
                                    </Link>
                                )}
                            </GridItem>
                            <GridItem {...styles.orderLineCardInfoGridItem}>
                                <GridContainer {...styles.orderLineCardInfoGridContainer}>
                                    <GridItem {...styles.orderLineCardInfoDescriptionGridItem}>
                                        <GridContainer {...styles.productInfoBrandDescriptionGridContainer}>
                                            <GridItem
                                                {...styles.productInfoDescriptionGridItem}
                                                css={descriptionCustomStyle()}
                                            >
                                                <ProductDescription
                                                    product={orderLine}
                                                    extendedStyles={styles.productDescriptionStyles}
                                                    sourceName="Order"
                                                />
                                            </GridItem>
                                            <GridItem {...styles.manufacturerSectionGridItem}>
                                                {shipmentPackageLine?.isActiveProduct && (
                                                    <>
                                                        <Typography {...styles.manufacturerItemStyles}>
                                                            {shipmentPackageLine?.manufacturerItem !== undefined && (
                                                                <>
                                                                    <Typography
                                                                        as="span"
                                                                        css={manufacturerCustomStyle()}
                                                                    >
                                                                        {orderLine?.manufacturerItem}&nbsp;|&nbsp;
                                                                    </Typography>
                                                                    <Typography as="span">
                                                                        {orderLine?.productErpNumber}
                                                                    </Typography>
                                                                </>
                                                            )}
                                                        </Typography>
                                                    </>
                                                )}
                                                {!shipmentPackageLine?.isActiveProduct && (
                                                    <>
                                                        <Typography {...styles.manufacturerItemStyles}>
                                                            {shipmentPackageLine?.manufacturerItem !== undefined && (
                                                                <>
                                                                    <Typography
                                                                        as="span"
                                                                        css={manufacturerCustomStyle()}
                                                                    >
                                                                        {orderLine?.manufacturerItem}&nbsp;|&nbsp;
                                                                    </Typography>
                                                                    <Typography as="span">
                                                                        {orderLine?.productErpNumber}
                                                                    </Typography>
                                                                </>
                                                            )}
                                                        </Typography>
                                                    </>
                                                )}                                                
                                            </GridItem>
                                            <GridItem {...styles.manufacturerSectionGridItem}>
                                            {(shipmentPackageLine?.altText === "N" || orderLine.properties["classificatioN_CODE"]==="N" ) && (
                                                    <Typography {...styles.manufacturerItemStyles}>
                                                        {translate("Non-Returnable")}
                                                    </Typography>
                                                )}
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                    <GridItem {...styles.qtyFieldGridItem}>
                                        <Typography>{shipmentPackageLine?.qtyShipped}</Typography>
                                    </GridItem>
                                    <GridItem {...styles.unitPriceGridItem}>
                                        <Typography>
                                            ${shipmentPackageLine?.unitPrice}
                                            {shipmentPackageLine?.brand != "" && (
                                                <Typography as="span">
                                                    {getunitOfMeasureDisplay(shipmentPackageLine?.brand)}
                                                </Typography>
                                            )}
                                        </Typography>
                                    </GridItem>
                                    <GridItem {...styles.extPriceGridItem}>
                                        <Typography>{shipmentPackageLine?.lineTotalDisplay}</Typography>
                                    </GridItem>
                                    {/* Eco fee --P21 Changes */}
                                    {shipmentPackageLine?.properties?.ecoFeeDisplay != "$0.00" && (
                                        <ADIEcoFeeDisplay orderLine={shipmentPackageLine} />
                                    )}
                                    {shipmentPackageLine?.properties?.specialHandlingFeeDisplay != "$0.00" && (
                                        <ADISpecialHandlingFeeDisplay orderLine={shipmentPackageLine} />
                                    )}
                                    <GridItem {...styles.desktopBuyItAgainButton}>
                                        {canReorderItems && orderLine && orderLine?.canAddToCart && (
                                            <ADIBuyItAgainButton product={product} orderLine={orderLine} />
                                        )}
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...styles.orderLineCardImageGridItemMobile}>
                                <Link
                                    {...styles.productImageLinkMobile}
                                    href={`/product/${shipmentPackageLine?.productErpNumber}`}
                                >
                                    {shipmentPackageLine?.properties?.smallImagePath?.indexOf("SM_NotFound.jpg") ===
                                        -1 && (
                                        <LazyImage
                                            {...styles.orderLineCardImageMobile}
                                            src={shipmentPackageLine?.properties?.smallImagePath}
                                            isShareEntity={isShareEntity}
                                        />
                                    )}
                                </Link>
                                {(shipmentPackageLine?.properties?.smallImagePath === undefined ||
                                    shipmentPackageLine?.properties?.smallImagePath.indexOf("SM_NotFound.jpg") !==
                                        -1) && (
                                    <Typography {...styles.imgTextStylesMobile}>
                                        {shipmentPackageLine?.ManufacturerItem}
                                    </Typography>
                                )}
                                <StyledWrapper {...styles.manufacturerItemContainerMobileStyles}>
                                    <Typography {...styles.manufacturerItemMobileStyles}>
                                        {shipmentPackageLine?.manufacturerItem !== undefined && (
                                            <>
                                                <Typography as="p" css={manufacturerCustomStyle()}>
                                                    {orderLine?.manufacturerItem}
                                                </Typography>
                                                <Typography as="p">{orderLine?.productErpNumber}</Typography>
                                            </>
                                        )}
                                    </Typography>
                                    {shipmentPackageLine?.altText === "N" && (
                                        <Typography {...styles.manufacturerItemMobileStyles}>
                                            {translate("Non-Returnable")}
                                        </Typography>
                                    )}
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.orderLineCardInfoGridItemMobile}>
                                <Link
                                    {...styles.productDescriptionStylesMobile}
                                    href={`/product/${shipmentPackageLine.productErpNumber}`}
                                >
                                    {orderLine?.shortDescription}
                                </Link>
                                <Typography as="p" {...styles.extPriceMobileStyles}>
                                    {shipmentPackageLine?.lineTotalDisplay}
                                </Typography>
                                <StyledWrapper {...styles.unitPriceMobileStyles}>
                                    <Typography>{translate("(Unit Price:")}&nbsp;</Typography>
                                    <Typography>
                                        {`${shipmentPackageLine?.unitPriceDisplay})`}
                                        {shipmentPackageLine?.brand != "" && (
                                            <Typography as="span">
                                                {getunitOfMeasureDisplay(shipmentPackageLine?.brand)}
                                            </Typography>
                                        )}
                                    </Typography>
                                </StyledWrapper>
                                <StyledWrapper {...styles.qtyMobileStyles}>
                                    <Typography>{translate("Qty:")}&nbsp;</Typography>
                                    <Typography>{shipmentPackageLine?.qtyShipped}</Typography>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.mobileBuyItAgainButton}>
                                {canReorderItems && orderLine && orderLine?.canAddToCart && (
                                    <ADIBuyItAgainButton product={product} orderLine={orderLine} />
                                )}
                            </GridItem>
                        </GridContainer>
                    )}
                </>
            }
            {isAdonisYes && (
                <GridContainer {...styles.orderLineCardGridContainer} key={index}>
                    <GridItem {...styles.orderLineCardImageGridItem}>
                        {(shipmentPackageLine?.properties?.smallImagePath?.indexOf("SM_NotFound.jpg") === -1 ||
                            shipmentPackageLine?.properties?.smallImagePath !== undefined) && (
                            <LazyImage
                                {...styles.orderLineCardImage}
                                src={shipmentPackageLine?.properties?.smallImagePath}
                                isShareEntity={isShareEntity}
                            />
                        )}
                        {shipmentPackageLine !== undefined &&
                            shipmentPackageLine?.properties?.smallImagePath === undefined && (
                                <Typography {...styles.imgTextStyles}>
                                    {shipmentPackageLine?.productErpNumber}
                                </Typography>
                            )}
                        <Link {...styles.productImageLink}>
                            {" "}
                            {shipmentPackageLine?.properties?.smallImagePath &&
                            !shipmentPackageLine.properties.smallImagePath.includes("SM_NotFound.jpg") ? (
                                <LazyImage
                                    {...styles.orderLineCardImage}
                                    src={shipmentPackageLine.properties.smallImagePath}
                                    isShareEntity={isShareEntity}
                                />
                            ) : (
                                <Typography {...styles.imgTextStyles}>
                                    {" "}
                                    {shipmentPackageLine?.ManufacturerItem}{" "}
                                </Typography>
                            )}
                        </Link>
                    </GridItem>
                    <GridItem {...styles.orderLineCardInfoGridItem}>
                        <GridContainer {...styles.orderLineCardInfoGridContainer}>
                            <GridItem {...styles.orderLineCardInfoDescriptionGridItem}>
                                <GridContainer {...styles.productInfoBrandDescriptionGridContainer}>
                                    <GridItem {...styles.productInfoDescriptionGridItem} css={descriptionCustomStyle()}>
                                        <ADIAdonisYesProductDescription
                                            product={orderLine}
                                            extendedStyles={styles.adiAdonisYesProductDescriptionStyles}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.manufacturerSectionGridItem}>
                                        <>
                                            <Typography {...styles.manufacturerItemStyles}>
                                                {shipmentPackageLine?.manufacturerItem !== undefined && (
                                                    <>
                                                        <Typography as="span" css={manufacturerCustomStyle()}>
                                                            {orderLine?.manufacturerItem}&nbsp;|&nbsp;
                                                        </Typography>
                                                        <Typography as="span">{orderLine?.productErpNumber}</Typography>
                                                    </>
                                                )}
                                            </Typography>
                                        </>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...styles.qtyFieldGridItem}>
                                <Typography>{shipmentPackageLine?.qtyShipped}</Typography>
                            </GridItem>
                            <GridItem {...styles.unitPriceGridItem}>
                                <Typography>
                                    ${shipmentPackageLine?.unitPrice}
                                    {shipmentPackageLine?.brand != "" && (
                                        <Typography as="span">
                                            {getunitOfMeasureDisplay(shipmentPackageLine?.brand)}
                                        </Typography>
                                    )}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.extPriceGridItem}>
                                <Typography>{shipmentPackageLine?.lineTotalDisplay}</Typography>
                            </GridItem>
                            {shipmentPackageLine?.properties?.ecoFeeDisplay != "$0.00" && (
                                <Typography>
                                    <ADIEcoFeeDisplay orderLine={shipmentPackageLine} />
                                </Typography>
                            )}
                            <GridItem {...styles.desktopBuyItAgainButton}>
                                {canReorderItems && orderLine && orderLine?.canAddToCart && (
                                    <ADIBuyItAgainButton product={product} orderLine={orderLine} />
                                )}
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.orderLineCardImageGridItemMobile}>
                        <Link
                            {...styles.productImageLinkMobile}
                            href={`/product/${shipmentPackageLine?.productErpNumber}`}
                        >
                            {(shipmentPackageLine?.properties?.smallImagePath?.indexOf("SM_NotFound.jpg") === -1 ||
                                shipmentPackageLine?.properties?.smallImagePath !== undefined) && (
                                <LazyImage
                                    {...styles.orderLineCardImageMobile}
                                    src={shipmentPackageLine?.properties?.smallImagePath}
                                    isShareEntity={isShareEntity}
                                />
                            )}
                        </Link>
                        {shipmentPackageLine?.properties?.smallImagePath === undefined &&
                            shipmentPackageLine !== undefined && (
                                <Typography {...styles.imgTextStylesMobile}>
                                    {shipmentPackageLine?.productErpNumber}
                                </Typography>
                            )}
                        <StyledWrapper {...styles.manufacturerItemContainerMobileStyles}>
                            <Typography {...styles.manufacturerItemMobileStyles}>
                                {shipmentPackageLine?.manufacturerItem !== undefined && (
                                    <>
                                        <Typography as="p" css={manufacturerCustomStyle()}>
                                            {orderLine?.manufacturerItem}
                                        </Typography>
                                        <Typography as="p">{orderLine?.productErpNumber}</Typography>
                                    </>
                                )}
                            </Typography>
                            {shipmentPackageLine?.altText === "N" && (
                                <Typography {...styles.manufacturerItemMobileStyles}>
                                    {translate("Non-Returnable")}
                                </Typography>
                            )}
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.orderLineCardInfoGridItemMobile}>
                        <Link
                            {...styles.productDescriptionStylesMobile}
                            href={`/product/${shipmentPackageLine.productErpNumber}`}
                        >
                            {orderLine?.shortDescription}
                        </Link>
                        <Typography {...styles.extPriceMobileStyles}>
                            {shipmentPackageLine?.lineTotalDisplay}
                        </Typography>
                        <StyledWrapper {...styles.unitPriceMobileStyles}>
                            <Typography>{translate("(Unit Price:")}&nbsp;</Typography>
                            <Typography>
                                {`${shipmentPackageLine?.unitPriceDisplay})`}
                                {shipmentPackageLine?.brand != "" && (
                                    <Typography as="span">
                                        {getunitOfMeasureDisplay(shipmentPackageLine?.brand)}
                                    </Typography>
                                )}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.qtyMobileStyles}>
                            <Typography>{translate("Qty:")}&nbsp;</Typography>
                            <Typography>{shipmentPackageLine?.qtyShipped}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.mobileBuyItAgainButton}>
                        {canReorderItems && orderLine && orderLine?.canAddToCart && (
                            <ADIBuyItAgainButton product={product} orderLine={orderLine} />
                        )}
                    </GridItem>
                </GridContainer>
            )}
        </>
    );
};

export default connect(mapStateToProps)(ADIOrderDetailsOrderLinesList);
